import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchSeries,
  deleteSeries,
  updateSeries,
} from "../redux/slices/seriesSlice";
import ConfirmModal from "./ConfirmModal";
import EditSeriesModal from "./EditSeriesModal";

const ITEMS_PER_PAGE = 10;

// Normaliza texto: pasa a minúsculas y elimina tildes/diacríticos
const normalizeText = (str = "") =>
  str
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita diacríticos (á->a, ó->o, etc.)
    .toLowerCase()
    .trim();

const SeriesTable = () => {
  const dispatch = useDispatch();
  const { items: series, status, error } = useSelector((state) => state.series);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [editingSeries, setEditingSeries] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [seriesToDelete, setSeriesToDelete] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  // Carga inicial
  useEffect(() => {
    dispatch(fetchSeries());
  }, [dispatch]);

  // Manejo de errores del slice
  useEffect(() => {
    if (!error) return;
    setErrorMessage(error);
    setShowError(true);
    const t = setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 5000);
    return () => clearTimeout(t);
  }, [error]);

  // Filtrado memoizado (ignora mayúsculas y tildes)
  const filteredSeries = useMemo(() => {
    if (!series?.length) return [];
    const term = normalizeText(searchTerm);
    if (!term) return series;

    return series.filter((s) => {
      const nameNorm = normalizeText(s?.name || "");
      // Si querés que también busque por género, descomentá las 2 líneas de abajo:
      // const genresRaw = Array.isArray(s?.genres) ? s.genres.join(", ") : (s?.genres || s?.genre || "");
      // const genresNorm = normalizeText(genresRaw);
      return nameNorm.includes(term); // || genresNorm.includes(term)
    });
  }, [series, searchTerm]);

  // Paginación derivada
  const totalPages = Math.max(
    1,
    Math.ceil(filteredSeries.length / ITEMS_PER_PAGE)
  );
  const currentSeries = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSeries.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSeries, currentPage]);

  // Clamp de página si cambian los resultados (búsqueda, delete, refresh)
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Reset a página 1 al cambiar el término de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEdit = (row) => {
    setEditingSeries(row);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSeries(null);
  };

  const handleSaveSeries = async (data) => {
    try {
      await dispatch(updateSeries(data)).unwrap();
      setIsEditModalOpen(false);
      setEditingSeries(null);

      setErrorMessage("Serie actualizada exitosamente");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
        setErrorMessage("");
      }, 3000);

      dispatch(fetchSeries());
    } catch (err) {
      console.error("Error al actualizar:", err);
      setErrorMessage("Error al actualizar la serie: " + (err?.message || ""));
      setShowError(true);
    }
  };

  const handleDelete = (row) => {
    setSeriesToDelete(row);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!seriesToDelete) return;
    try {
      const resultAction = await dispatch(deleteSeries(seriesToDelete.id));
      if (deleteSeries.rejected.match(resultAction)) {
        setErrorMessage(resultAction.payload?.message || "No se pudo eliminar");
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
          setErrorMessage("");
        }, 5000);
      } else {
        dispatch(fetchSeries());
      }
    } catch (err) {
      console.error("Error al eliminar:", err);
      setErrorMessage("Error al eliminar la serie");
      setShowError(true);
    } finally {
      setIsConfirmModalOpen(false);
      setSeriesToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmModalOpen(false);
    setSeriesToDelete(null);
  };

  // Paginador simple con números
  const renderPagination = () => {
    if (filteredSeries.length <= ITEMS_PER_PAGE) return null;

    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return (
      <div className="flex justify-center mt-4">
        <nav>
          <ul className="flex">
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`mx-1 px-3 py-1 rounded ${
                  currentPage === p
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {p}
              </button>
            ))}
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {showError && (
        <div className="bg-red-600 text-white p-4 rounded-md mb-4 text-center font-bold">
          {errorMessage}
        </div>
      )}

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="Confirmar eliminación"
        message={
          seriesToDelete
            ? `¿Estás seguro de que deseas eliminar la serie "${seriesToDelete.name}"?\n\nRecuerda que si tiene temporadas asociadas, la eliminación no será posible.`
            : "¿Estás seguro de que deseas eliminar esta serie?"
        }
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Modal de edición */}
      {isEditModalOpen && (
        <EditSeriesModal
          isOpen={isEditModalOpen}
          series={editingSeries}
          onClose={closeEditModal}
          onSave={handleSaveSeries}
        />
      )}

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar series..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-600 rounded-md px-4 py-2 w-64 bg-gray-800 text-gray-300"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-900 divide-y divide-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Título
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Género
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Año
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Temporadas
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>

          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {currentSeries.length > 0 ? (
              currentSeries.map((row) => (
                <tr key={row.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                    {row.id ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-white font-medium">
                    {row.name ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-800 text-blue-100">
                      {(() => {
                        const raw = row.genres ?? row.genre;
                        if (typeof raw === "string") return raw || "—";
                        if (Array.isArray(raw)) return raw.join(", ") || "—";
                        return "—";
                      })()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                    {row.releaseYear ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">
                    {row.totalSeasons ?? "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap space-x-3">
                    <button
                      onClick={() => handleEdit(row)}
                      className="text-blue-400 hover:text-blue-200"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(row)}
                      className="text-red-400 hover:text-red-200"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-white">
                  {status === "loading"
                    ? "Cargando..."
                    : "No hay series disponibles"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default SeriesTable;

// import React, { useState, useEffect } from 'react';
// import { useSelector, useDispatch } from 'react-redux';
// import { fetchSeries, deleteSeries, updateSeries } from '../redux/slices/seriesSlice';
// import ConfirmModal from './ConfirmModal';
// import EditSeriesModal from './EditSeriesModal';

// const SeriesTable = () => {
//   const dispatch = useDispatch();
//   const { items: series, status, error } = useSelector((state) => state.series);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(10);
//   const [editingSeries, setEditingSeries] = useState(null);
//   const [errorMessage, setErrorMessage] = useState('');
//   const [showError, setShowError] = useState(false);

//   // Estado para el modal de confirmación
//   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
//   const [seriesToDelete, setSeriesToDelete] = useState(null);

//   // Estado para el modal de edición
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);

//   useEffect(() => {
//     dispatch(fetchSeries());
//   }, [dispatch]);

//   // Mostrar error si existe
//   useEffect(() => {
//     if (error) {
//       setErrorMessage(error);
//       setShowError(true);
//       // Ocultar el mensaje después de 5 segundos
//       const timer = setTimeout(() => {
//         setShowError(false);
//         setErrorMessage('');
//       }, 5000);
//       return () => clearTimeout(timer);
//     }
//   }, [error]);

//   // Filtrar series por término de búsqueda
//   const filteredSeries = series && series.length ? series.filter(series =>
//     series.name?.toLowerCase().includes(searchTerm.toLowerCase())
//   ) : [];

//   // Calcular paginación
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentSeries = filteredSeries.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(filteredSeries.length / itemsPerPage);

//   const handlePageChange = (pageNumber) => {
//     setCurrentPage(pageNumber);
//   };

//   const handleEdit = (series) => {
//     setEditingSeries(series);
//     setIsEditModalOpen(true);
//   };

//   const handleSaveSeries = async (data) => {
//     try {
//       await dispatch(updateSeries(data)).unwrap();
//       setIsEditModalOpen(false);
//       setEditingSeries(null);

//       // Mostrar mensaje de éxito
//       setErrorMessage('Serie actualizada exitosamente');
//       setShowError(true);
//       setTimeout(() => {
//         setShowError(false);
//         setErrorMessage('');
//       }, 3000);

//       // Recargar la lista de series
//       dispatch(fetchSeries());
//     } catch (err) {
//       console.error('Error al actualizar:', err);
//       setErrorMessage('Error al actualizar la serie: ' + (err.message || ''));
//       setShowError(true);
//     }
//   };

//   const closeEditModal = () => {
//     setIsEditModalOpen(false);
//     setEditingSeries(null);
//   };

//   const handleDelete = (series) => {
//     setSeriesToDelete(series);
//     setIsConfirmModalOpen(true);
//   };

//   const confirmDelete = async () => {
//     if (!seriesToDelete) return;

//     try {
//       const resultAction = await dispatch(deleteSeries(seriesToDelete.id));
//       if (deleteSeries.rejected.match(resultAction)) {
//         // La acción fue rechazada, mostrar mensaje de error
//         setErrorMessage(resultAction.payload.message);
//         setShowError(true);
//         // Ocultar el mensaje después de 5 segundos
//         setTimeout(() => {
//           setShowError(false);
//           setErrorMessage('');
//         }, 5000);
//       } else {
//         // La acción fue exitosa, refrescar la lista
//         dispatch(fetchSeries());
//       }
//     } catch (err) {
//       console.error('Error al eliminar:', err);
//       setErrorMessage('Error al eliminar la serie');
//       setShowError(true);
//     } finally {
//       // Cerrar el modal y limpiar el estado
//       setIsConfirmModalOpen(false);
//       setSeriesToDelete(null);
//     }
//   };

//   const cancelDelete = () => {
//     setIsConfirmModalOpen(false);
//     setSeriesToDelete(null);
//   };

//   const renderPagination = () => {
//     const pages = [];
//     for (let i = 1; i <= totalPages; i++) {
//       pages.push(
//         <button
//           key={i}
//           onClick={() => handlePageChange(i)}
//           className={`mx-1 px-3 py-1 rounded ${
//             currentPage === i
//               ? 'bg-blue-500 text-white'
//               : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//           }`}
//         >
//           {i}
//         </button>
//       );
//     }
//     return pages;
//   };

//   return (
//     <div className="space-y-4">
//       {showError && (
//         <div className="bg-red-600 text-white p-4 rounded-md mb-4 text-center font-bold">
//           {errorMessage}
//         </div>
//       )}

//       {/* Modal de confirmación */}
//       <ConfirmModal
//         isOpen={isConfirmModalOpen}
//         title="Confirmar eliminación"
//         message={
//           seriesToDelete
//             ? `¿Estás seguro de que deseas eliminar la serie "${seriesToDelete.name}"?\n\nRecuerda que si tiene temporadas asociadas, la eliminación no será posible.`
//             : "¿Estás seguro de que deseas eliminar esta serie?"
//         }
//         onConfirm={confirmDelete}
//         onCancel={cancelDelete}
//       />

//       {/* Modal de edición */}
//       {isEditModalOpen && (
//         <EditSeriesModal
//           isOpen={isEditModalOpen}
//           series={editingSeries}
//           onClose={closeEditModal}
//           onSave={handleSaveSeries}
//         />
//       )}

//       <div className="mb-4">
//         <input
//           type="text"
//           placeholder="Buscar series..."
//           value={searchTerm}
//           onChange={(e) => setSearchTerm(e.target.value)}
//           className="border border-gray-600 rounded-md px-4 py-2 w-64 bg-gray-800 text-gray-300"
//         />
//       </div>

//       <div className="overflow-x-auto">
//         <table className="min-w-full bg-gray-900 divide-y divide-gray-700">
//           <thead className="bg-gray-800">
//             <tr>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Título</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Género</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Año</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Temporadas</th>
//               <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Acciones</th>
//             </tr>
//           </thead>
//           <tbody className="bg-gray-800 divide-y divide-gray-700">
//             {currentSeries.length > 0 ? (
//               currentSeries.map(series => (
//                 <tr key={series.id} className="hover:bg-gray-700">
//                   <td className="px-6 py-4 whitespace-nowrap text-gray-300">{series.id || 'ID no disponible'}</td>
//                   <td className="px-6 py-4 whitespace-nowrap text-white font-medium">{series.name || 'Nombre no disponible'}</td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <span className="px-4 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-800 text-blue-100">
//                       {typeof series.genre === 'string'
//                         ? series.genre
//                         : Array.isArray(series.genre)
//                           ? series.genre.join(', ')
//                           : 'Género no disponible'}
//                     </span>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-gray-300">{series.releaseYear || 'Año no disponible'}</td>
//                   <td className="px-6 py-4 whitespace-nowrap text-gray-300">{series.totalSeasons || 'Temporadas no disponibles'}</td>
//                   <td className="px-6 py-4 whitespace-nowrap space-x-2">
//                     <button
//                       onClick={() => handleEdit(series)}
//                       className="text-blue-400 hover:text-blue-200"
//                     >
//                       Editar
//                     </button>
//                     <button
//                       onClick={() => handleDelete(series)}
//                       className="text-red-400 hover:text-red-200"
//                     >
//                       Eliminar
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="px-6 py-4 text-center text-white">
//                   {status === 'loading' ? 'Cargando...' : 'No hay series disponibles'}
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {totalPages > 1 && (
//         <div className="flex justify-center mt-4">
//           <nav>
//             <ul className="flex">
//               {renderPagination()}
//             </ul>
//           </nav>
//         </div>
//       )}
//     </div>
//   );
// };

// export default SeriesTable;
